import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Container,
  Paper,
  Grid,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Zoom,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel
} from '@mui/material';
import { 
  PlayArrow, 
  Stop, 
  Casino, 
  ArrowForward,
  MonetizationOn,
  ContentCopy 
} from '@mui/icons-material';

const TipsSection = () => (
  <Paper 
    elevation={3} 
    sx={{ 
      p: 3, 
      borderRadius: 2,
      background: 'linear-gradient(145deg, rgba(26, 35, 126, 0.05), rgba(13, 71, 161, 0.1))',
      border: '1px solid rgba(26, 35, 126, 0.1)',
      mb: 4
    }}
  >
    <Typography 
      variant="h6" 
      gutterBottom 
      sx={{ 
        fontWeight: 600,
        color: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}
    >
      <Casino /> Pro Tips to Beat the Bookie
    </Typography>
    <Box sx={{ mt: 2 }}>
      {[
        `Wait for 15 minutes after each win before playing again.`,
        `Use different betting sites daily to avoid pattern detection.`,
        `Close your account once you've doubled your initial capital.`
      ].map((tip, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 1.5,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            border: '1px solid rgba(26, 35, 126, 0.1)',
            transition: 'transform 0.2s ease',
            '&:hover': {
              transform: 'translateX(5px)',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
            }
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <ArrowForward sx={{ color: 'primary.main', fontSize: 20 }} />
            {tip}
          </Typography>
        </Box>
      ))}
    </Box>
  </Paper>
);

const MartingaleSection = () => {
  const [capital, setCapital] = useState('');
  const [unitSize, setUnitSize] = useState(null);
  const [maxRounds, setMaxRounds] = useState(0);

  const calculateUnitSize = (totalCapital) => {
    const safeCapital = totalCapital * 0.95;
    let unitSize = Math.floor(safeCapital / 2047) * 0.1;
    return Math.max(0.1, unitSize);
  };

  const calculateMaxRounds = (capital, unit) => {
    let total = 0;
    let rounds = 0;
    const multipliers = [1, 3, 7, 15, 31, 63, 127, 255, 511, 1023];
    
    for (let multiplier of multipliers) {
      total += (multiplier * unit * 2);
      if (total <= capital) {
        rounds++;
      } else {
        break;
      }
    }
    return rounds;
  };

  const handleCapitalChange = (event) => {
    const value = event.target.value;
    setCapital(value);
    if (value > 0) {
      const unit = calculateUnitSize(value);
      setUnitSize(unit);
      setMaxRounds(calculateMaxRounds(value, unit));
    } else {
      setUnitSize(null);
      setMaxRounds(0);
    }
  };

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 3, 
        borderRadius: 2,
        background: 'linear-gradient(145deg, rgba(26, 35, 126, 0.05), rgba(13, 71, 161, 0.1))',
        border: '1px solid rgba(26, 35, 126, 0.1)',
        mb: 4
      }}
    >
      <Typography 
        variant="h6" 
        gutterBottom 
        sx={{ 
          fontWeight: 600,
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Casino /> Column Martingale Strategy
      </Typography>

      {/* Capital Input Section */}
      <Box sx={{ 
        mb: 3,
        p: 2,
        bgcolor: 'rgba(255, 255, 255, 0.7)',
        borderRadius: 1,
        border: '1px solid rgba(26, 35, 126, 0.1)',
      }}>
        <Typography variant="subtitle2" color="primary" fontWeight="600" gutterBottom>
          Calculate Your Two-Column Martingale Units:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
          <TextField
            label="Enter Your Capital"
            type="number"
            value={capital}
            onChange={handleCapitalChange}
            variant="outlined"
            size="small"
            sx={{ minWidth: 150 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
        </Box>
        {unitSize && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ p: 1, bgcolor: 'rgba(25, 118, 210, 0.1)', borderRadius: 1, mb: 1 }}>
              <Typography variant="body2" color="primary" fontWeight="medium">
                Recommended Unit Size: ${unitSize.toFixed(1)} per column (${(unitSize * 2).toFixed(1)} total per round)
              </Typography>
            </Box>
            {maxRounds < 10 && (
              <Box sx={{ 
                p: 1, 
                bgcolor: 'rgba(211, 47, 47, 0.1)', 
                borderRadius: 1,
                border: '1px solid rgba(211, 47, 47, 0.3)'
              }}>
                <Typography variant="body2" color="error" fontWeight="medium">
                  Warning: Your capital allows for {maxRounds} rounds of Martingale progression.
                  Consider using a smaller unit size or increasing your capital.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Martingale Progression */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" color="primary" fontWeight="600" gutterBottom>
          Two-Column Betting Progression {unitSize ? `(Starting Unit = $${unitSize.toFixed(1)})` : ''}:
        </Typography>
        <Box sx={{ pl: 2 }}>
          {[1, 3, 7, 15, 31, 63, 127, 255, 511, 1023].map((multiplier, index) => {
            const isAffordable = index < maxRounds;
            const columnBet = multiplier * unitSize;
            const totalBet = columnBet * 2;
            const winReturn = columnBet * 3; // 2:1 payout plus original bet on winning column
            const netWin = winReturn - totalBet; // Subtract total investment from return

            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  mb: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: isAffordable ? 'rgba(255, 255, 255, 0.7)' : 'rgba(211, 47, 47, 0.1)',
                  border: `1px solid ${isAffordable ? 'rgba(26, 35, 126, 0.1)' : 'rgba(211, 47, 47, 0.3)'}`,
                  transition: 'transform 0.2s ease',
                  opacity: isAffordable ? 1 : 0.7,
                  '&:hover': isAffordable ? {
                    transform: 'translateX(5px)',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  } : {}
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: isAffordable ? 'primary.main' : 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <ArrowForward sx={{ color: isAffordable ? 'primary.main' : 'error.main', fontSize: 16 }} />
                  {index === 0 ? 'Start' : `Loss ${index}`}
                </Typography>

                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pl: 3
                }}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: isAffordable ? 'text.secondary' : 'error.light',
                      }}
                    >
                      Column 1: ${columnBet.toFixed(1)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: isAffordable ? 'text.secondary' : 'error.light',
                      }}
                    >
                      Column 2: ${columnBet.toFixed(1)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography
                      variant="body2"
                      sx={{ 
                        fontWeight: 600,
                        color: isAffordable ? 'primary.main' : 'error.main',
                      }}
                    >
                      Total Bet: ${totalBet.toFixed(1)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ 
                        color: isAffordable ? 'success.main' : 'error.light',
                      }}
                    >
                      Net Win: ${netWin.toFixed(1)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Important Notes */}
      <Box sx={{ 
        mt: 3, 
        p: 2, 
        bgcolor: 'rgba(25, 118, 210, 0.1)',
        borderRadius: 1
      }}>
        <Typography variant="subtitle2" color="primary" fontWeight="600" gutterBottom>
          Important Notes:
        </Typography>
        <Typography variant="body2" paragraph>
          • Starting bet is equal on both columns
        </Typography>
        <Typography variant="body2" paragraph>
          • Minimum bet is $0.1 per column ($0.2 total)
        </Typography>
        <Typography variant="body2" paragraph>
          • After each loss, increase bet to cover previous losses
        </Typography>
        <Typography variant="body2" paragraph>
          • After a win on either column, return to starting bet
        </Typography>
        <Typography variant="body2" paragraph>
          • Each column pays 2:1 when won
        </Typography>
        <Typography variant="body2">
          • Always check table limits before starting
        </Typography>
      </Box>
    </Paper>
  );
};

const RouletteBoard = ({ onColumnSelect, disabled, activeColumn }) => {
  const columns = [
    [3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36],  // Column 3-36
    [2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35],  // Column 2-35
    [1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34]   // Column 1-34
  ];
  const isMobile = useMediaQuery('(max-width:600px)');

  const getColumnColor = (idx) => {
    const colors = {
      0: { main: '#d32f2f', hover: '#e53935', light: '#ffcdd2' },
      1: { main: '#1976d2', hover: '#1e88e5', light: '#bbdefb' },
      2: { main: '#2e7d32', hover: '#43a047', light: '#c8e6c9' }
    };
    return colors[idx];
  };

  return (
    <Box sx={{ 
      width: '100%', 
      display: 'flex',
      flexDirection: 'row',
      gap: 3,
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'auto',
      px: 2
    }}>
      {/* Board */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: { xs: '320px', sm: '500px', md: '600px' },
        width: '100%',
        maxWidth: '800px'
      }}>
        {columns.map((column, idx) => (
          <Button
            key={idx}
            onClick={() => onColumnSelect(idx + 1)}
            disabled={disabled}
            sx={{
              width: '100%',
              height: { xs: '50px', sm: '60px' },
              background: `linear-gradient(145deg, ${getColumnColor(idx).main}, ${getColumnColor(idx).hover})`,
              color: 'white',
              display: 'flex',
              flexDirection: 'row',
              gap: { xs: 0.5, sm: 1 },
              p: { xs: 0.5, sm: 1 },
              justifyContent: 'space-evenly',
              alignItems: 'center',
              '&:hover': {
                background: `linear-gradient(145deg, ${getColumnColor(idx).hover}, ${getColumnColor(idx).main})`,
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              },
              '&:disabled': {
                opacity: 0.7,
                color: 'white'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              borderRadius: 2,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              opacity: activeColumn === idx + 1 ? 1 : 0.9,
              transform: activeColumn === idx + 1 ? 'scale(1.02)' : 'scale(1)',
              overflow: 'hidden'
            }}
          >
            {column.map(num => (
              <Box
                key={num}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  borderRadius: 1,
                  padding: { xs: '2px 4px', sm: '4px 6px' },
                  minWidth: { xs: '28px', sm: '35px' },
                  height: { xs: '28px', sm: '35px' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.25)',
                    transform: 'scale(1.1)',
                  },
                  flexShrink: 0
                }}
              >
                <Typography 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    textShadow: '0 1px 1px rgba(0,0,0,0.2)',
                    letterSpacing: '0.5px',
                    lineHeight: 1
                  }}
                >
                  {num}
                </Typography>
              </Box>
            ))}
          </Button>
        ))}
      </Box>

      {/* Labels on the right */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1, sm: 1.5 },
        justifyContent: 'center',
        minWidth: 'fit-content'
      }}>
        {[
          '3-36 Column',
          '2-35 Column',
          '1-34 Column'
        ].map((label, idx) => (
          <Typography
            key={idx}
            sx={{
              color: getColumnColor(idx).main,
              fontWeight: 600,
              fontSize: { xs: '0.8rem', sm: '0.9rem' },
              whiteSpace: 'nowrap',
              height: { xs: '50px', sm: '60px' },
              display: 'flex',
              alignItems: 'center',
              pl: 2
            }}
          >
            {label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

const RoundDisplay = ({ round, index, isLatest }) => (
  <Fade in={true}>
    <Typography 
      sx={{
        p: 1.5,
        borderRadius: 2,
        mb: 1,
        background: isLatest ? 'linear-gradient(45deg, rgba(26, 35, 126, 0.1), rgba(13, 71, 161, 0.1))' : 'rgba(0, 0, 0, 0.05)',
        border: isLatest ? '1px solid rgba(26, 35, 126, 0.2)' : 'none',
        transform: isLatest ? 'scale(1.02)' : 'scale(1)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      <span>Round {index + 1}: Column {round}</span>
      {isLatest && <ArrowForward sx={{ color: 'primary.main' }} />}
    </Typography>
  </Fade>
);

const PaymentModal = ({ open, onClose }) => {
  const btcAddress = "bc1pqqcrved6nsv3v6ulyj5apykaq45lwhhkftkx3gsdu65dmhedlxdshqw3l3";
  const telegramUsername = "unemployed_developer";

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(btcAddress);
  };

  const handleCopyTelegram = () => {
    navigator.clipboard.writeText(telegramUsername);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle sx={{ 
        background: 'linear-gradient(45deg, #1a237e 30%, #0d47a1 90%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <MonetizationOn /> Premium Access Required
      </DialogTitle>
      <DialogContent sx={{ mt: 2, minWidth: 300 }}>
        <Typography variant="body1" gutterBottom>
          To continue using Advanced Roulette AI, please make a payment of:
        </Typography>
        <Box sx={{ 
          my: 3, 
          p: 2, 
          bgcolor: 'rgba(25, 118, 210, 0.1)',
          borderRadius: 1,
          textAlign: 'center'
        }}>
          <Typography variant="h5" color="primary" fontWeight="bold">
            0.02 BTC
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Send payment to:
        </Typography>
        <Box sx={{ 
          mt: 1,
          p: 2,
          bgcolor: '#f5f5f5',
          borderRadius: 1,
          wordBreak: 'break-all',
          fontFamily: 'monospace'
        }}>
          {btcAddress}
        </Box>
        <Box sx={{ 
          mt: 3,
          p: 2,
          bgcolor: '#e3f2fd',
          borderRadius: 1,
          border: '1px solid #90caf9'
        }}>
          <Typography variant="body2" color="primary" gutterBottom fontWeight="medium">
            After payment:
          </Typography>
          <Typography variant="body2">
            Message us on Telegram:
          </Typography>
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 1
          }}>
            <Typography 
              variant="body1" 
              fontWeight="medium"
              sx={{ 
                color: '#0088cc',
                fontFamily: 'monospace'
              }}
            >
              @{telegramUsername}
            </Typography>
            <IconButton 
              size="small" 
              onClick={handleCopyTelegram}
              sx={{ color: 'primary.main' }}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <Button 
          variant="contained"
          onClick={handleCopyAddress}
          startIcon={<ContentCopy />}
          sx={{ mr: 2 }}
        >
          Copy Address
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Home = () => {
  const [currentPredictionIndex, setCurrentPredictionIndex] = useState(0);
  const [betHistory, setBetHistory] = useState([]);

  const [capital, setCapital] = useState('');
  const [unitSize, setUnitSize] = useState(null);
  const [currentBetMultiplier, setCurrentBetMultiplier] = useState(1);
  const [showCapitalInput, setShowCapitalInput] = useState(false);
  const [predictionType, setPredictionType] = useState('2'); // Default to 2 columns
  
  const [isCollecting, setIsCollecting] = useState(false);
  const [rounds, setRounds] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [activeColumn, setActiveColumn] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasUsedFree, setHasUsedFree] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const multipliers = predictionType === '1' ? [1, 2, 4, 8, 16, 32, 64, 128, 256, 512] : [1, 3, 7, 15, 31, 63, 127, 255, 511, 1023];

  const calculateUnitSize = (totalCapital) => {
    const safeCapital = totalCapital * 0.95;
    let unitSize = Math.floor(safeCapital / (predictionType === '1' ? 1023 : 2047)) * 0.1;
    return Math.max(0.1, unitSize);
  };

  const handleCapitalSubmit = () => {
    if (capital > 0) {
      const unit = calculateUnitSize(capital);
      setUnitSize(unit);
      setCurrentBetMultiplier(1);
      setCurrentPredictionIndex(0);
      setBetHistory([]);
      setShowCapitalInput(false);
      setIsCollecting(true);
      setRounds([]);
      setPredictions([]);
      setActiveColumn(null);
    }
  };

  const handleStartStop = () => {
    if (isCollecting) {
      setIsCollecting(false);
      if (rounds.length >= 10) {
        generatePredictions();
      }
    } else {
      if (!hasUsedFree) {
        setShowCapitalInput(true);
      } else {
        setShowPayment(true);
      }
    }
  };

  const generatePredictions = async () => {
    setIsLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    const newPredictions = Array(10).fill(null).map(() => {
      if (predictionType === '1') {
        return { column1: Math.floor(Math.random() * 3) + 1 };
      } else {
        return {
          column1: Math.floor(Math.random() * 3) + 1,
          column2: Math.floor(Math.random() * 3) + 1
        };
      }
    });
    setPredictions(newPredictions);
    setIsLoading(false);
  };

  const addRound = (column) => {
    if (isCollecting && rounds.length < 10) {
      setActiveColumn(column);
      setRounds([...rounds, column]);
      if (rounds.length === 9) {
        setIsCollecting(false);
        setTimeout(generatePredictions, 1000);
      }
    }
  };

  // Check localStorage on component mount
  useEffect(() => {
    const hasUsed = localStorage.getItem('hasUsedRouletteAI');
    if (hasUsed) {
      setHasUsedFree(true);
    }
  }, []);

  const CapitalInputDialog = () => (
    <Dialog open={showCapitalInput} onClose={() => setShowCapitalInput(false)}>
      <DialogTitle sx={{ 
        background: 'linear-gradient(45deg, #1a237e 30%, #0d47a1 90%)',
        color: 'white'
      }}>
        Enter Your Capital and Prediction Type
      </DialogTitle>
      <DialogContent sx={{ mt: 2, minWidth: 300 }}>
        <TextField
          label="Capital Amount"
          type="number"
          value={capital}
          onChange={(e) => setCapital(e.target.value)}
          fullWidth
          variant="outlined"
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
        <FormControl component="fieldset" sx={{ mt: 3 }}>
          <FormLabel component="legend">Prediction Type</FormLabel>
          <RadioGroup
            value={predictionType}
            onChange={(e) => setPredictionType(e.target.value)}
          >
            <FormControlLabel value="1" control={<Radio />} label="Predict 1 column each round (Risky)" />
            <FormControlLabel value="2" control={<Radio />} label="Predict 2 columns each round" />
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowCapitalInput(false)}>Cancel</Button>
        <Button 
          variant="contained"
          onClick={handleCapitalSubmit}
          disabled={!capital || capital <= 0}
        >
          Start
        </Button>
      </DialogActions>
    </Dialog>
  );

  const handleWin = () => {
    setBetHistory([...betHistory, 'win']);
    setCurrentBetMultiplier(1); // Reset to initial bet
    setCurrentPredictionIndex(currentPredictionIndex + 1); // Move to next prediction
  };

  const handleLoss = () => {
    setBetHistory([...betHistory, 'loss']);
    const nextIndex = multipliers.indexOf(currentBetMultiplier) + 1;
    if (nextIndex < multipliers.length) {
      setCurrentBetMultiplier(multipliers[nextIndex]);
    }
    setCurrentPredictionIndex(currentPredictionIndex + 1);
  };

  const PredictionsDisplay = () => {
    const columnBet = currentBetMultiplier * unitSize;
    const totalBet = columnBet * (predictionType === '1' ? 1 : 2);
    const winReturn = columnBet * 3;
    const netWin = winReturn - totalBet;

    return (
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          borderRadius: 2,
          background: 'rgba(255, 255, 255, 0.9)',
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Predictions & Betting
        </Typography>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          predictions.map((pred, index) => {
            const isCurrentRound = index === currentPredictionIndex;
            const isPastRound = index < currentPredictionIndex;
            const roundResult = betHistory[index];

            return (
              <Fade in={true} key={index} style={{ transitionDelay: `${index * 100}ms` }}>
                <Box 
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    mb: 1,
                    background: isPastRound 
                      ? roundResult === 'win' 
                        ? 'rgba(76, 175, 80, 0.1)'
                        : 'rgba(244, 67, 54, 0.1)'
                      : 'rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(26, 35, 126, 0.1)',
                    opacity: isCurrentRound ? 1 : isPastRound ? 0.7 : 0.5
                  }}
                >
                  <Typography 
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 500,
                      mb: 1
                    }}
                  >
                    Round {index + 1}: Columns {pred.column1} {pred.column2 ? `or ${pred.column2}` : ''}
                  </Typography>
                  
                  {(isCurrentRound || isPastRound) && (
                    <Box sx={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 1
                    }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Bet per column: ${columnBet.toFixed(1)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total bet: ${totalBet.toFixed(1)}
                        </Typography>
                        {isPastRound && (
                          <Typography 
                            variant="body2" 
                            color={roundResult === 'win' ? 'success.main' : 'error.main'}
                            sx={{ mt: 0.5 }}
                          >
                            {roundResult === 'win' ? `Won: +$${netWin.toFixed(1)}` : 'Lost'}
                          </Typography>
                        )}
                      </Box>
                      {isCurrentRound && (
                        <Box>
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={handleWin}
                            sx={{ mr: 1 }}
                          >
                            Won
                          </Button>
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            onClick={handleLoss}
                          >
                            Lost
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Fade>
            );
          })
        )}
      </Paper>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
        py: 4,
        px: 2,
        transition: 'background 0.5s ease'
      }}
    >
      <Container maxWidth="lg">
        <Fade in={true}>
          <Paper 
            elevation={24}
            sx={{
              p: { xs: 2, sm: 4 },
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 3,
                  flexWrap: 'wrap',
                  justifyContent: 'center'
                }}
              >
                <Casino 
                  sx={{ 
                    fontSize: { xs: 30, sm: 40 }, 
                    color: theme.palette.primary.main,
                    animation: 'spin 4s linear infinite'
                  }}
                />
                <Typography 
                  variant={isMobile ? "h4" : "h3"}
                  sx={{
                    fontWeight: 700,
                    background: 'linear-gradient(45deg, #1a237e 30%, #0d47a1 90%)',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                  }}
                >
                  Advanced Roulette AI
                </Typography>
              </Box>
            </Box>

            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              gap: 2 
            }}>
              <Button
                onClick={handleStartStop}
                disabled={isLoading}
                variant="contained"
                startIcon={
                  isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : isCollecting ? (
                    <Stop />
                  ) : (
                    <PlayArrow />
                  )
                }
                sx={{
                  height: 48,
                  px: 4,
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: isCollecting 
                    ? '#d32f2f'
                    : '#1976d2',
                  color: 'white',
                  '&:hover': {
                    background: isCollecting
                      ? '#c62828'
                      : '#1565c0',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease',
                  borderRadius: '24px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  textTransform: 'none',
                  minWidth: 180,
                }}
              >
                {isCollecting ? 'Stop' : 'Start'}
              </Button>

              <Box sx={{ 
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {!isCollecting && rounds.length === 0 && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                  >
                    Click Start to begin
                  </Typography>
                )}

                {isCollecting && (
                  <Typography 
                    variant="body2"
                    sx={{ 
                      color: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontWeight: 500
                    }}
                  >
                    Round {rounds.length + 1} of 10
                    <ArrowForward fontSize="small" />
                  </Typography>
                )}
              </Box>
            </Box>

            <TipsSection />

            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Paper 
                  elevation={3} 
                  sx={{ 
                    p: { xs: 1, sm: 2, md: 3 },
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.9)',
                    mb: 4,
                    overflow: 'hidden'
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    width: '100%',
                    overflow: 'auto'
                  }}>
                    <RouletteBoard 
                      onColumnSelect={addRound}
                      disabled={!isCollecting || rounds.length >= 10}
                      activeColumn={activeColumn}
                    />
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper 
                  elevation={3} 
                  sx={{ 
                    p: 3, 
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.9)',
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Last {rounds.length}/10 Rounds
                  </Typography>
                  {rounds.map((round, index) => (
                    <RoundDisplay 
                      key={index}
                      round={round}
                      index={index}
                      isLatest={index === rounds.length - 1}
                    />
                  ))}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <PredictionsDisplay />
              </Grid>

              <Grid item xs={12}>
                <MartingaleSection />
              </Grid>
            </Grid>
          </Paper>
        </Fade>
      </Container>

      {/* Add the payment modal */}
      <PaymentModal 
        open={showPayment} 
        onClose={() => setShowPayment(false)}
      />

      <CapitalInputDialog />
    </Box>
  );
};

export default Home; 